<script setup lang="ts">
import CardTitle from '@/components/CardTitle.vue'
const { t } = useI18n()
const { segmentTypes, formatSegmentsByTypes } = useAudience()

withDefaults(
  defineProps<{
    customerLists?: {
      id: number
      name: string
      type: string
      frequency_type: string
    }[]
  }>(),
  {
    customerLists: () => []
  }
)
</script>

<template>
  <div class="mt-8 w-full rounded-lg border bg-white sm:mt-0">
    <div class="ml-4 flex items-center py-5 sm:ml-6">
      <CardTitle
        :title="t('pages.customer.customerDetailPage.blocks.segmentation.title')"
        subtitle=""
        name="HeroSquare3Stack3DIconOutline"
      />
    </div>

    <div class="border-t px-4 py-5 sm:px-6">
      <div v-if="customerLists.length" class="grid grid-cols-1 gap-4">
        <template v-for="segment in formatSegmentsByTypes(customerLists)" :key="segment.type">
          <div class="flex flex-col gap-3">
            <p class="text-sm font-medium text-muted-foreground">
              {{ segment.text }}
            </p>

            <div v-if="segment.values.length" class="flex flex-col gap-2">
              <template v-if="segment.values.length <= 5">
                <div v-for="item in segment.values" :key="item.id">
                  <router-link
                    class="flex items-center gap-x-2"
                    :to="{ name: 'AudienceDetail', params: { audienceId: item.id } }"
                  >
                    <BaseChip
                      :color="segmentTypes.find((type) => type.id === segment.type)?.color"
                      :icon="segmentTypes.find((type) => type.id === segment.type)?.icon"
                      :text="item.name"
                    />
                  </router-link>
                </div>
              </template>
              <template v-else>
                <div class="group relative">
                  <div class="flex items-center gap-x-2">
                    <router-link
                      class="flex items-center gap-x-2"
                      :to="{
                        name: 'AudienceDetail',
                        params: { audienceId: segment.values[0].id }
                      }"
                    >
                      <BaseChip
                        :color="segmentTypes.find((type) => type.id === segment.type)?.color"
                        :icon="segmentTypes.find((type) => type.id === segment.type)?.icon"
                        :text="segment.values[0].name"
                      />
                    </router-link>
                    <span
                      class="flex min-h-7 min-w-7 cursor-pointer items-center justify-center rounded-full border border-foreground bg-white text-xs text-muted-foreground"
                    >
                      +{{ segment.values.length - 1 }}
                    </span>
                  </div>

                  <div
                    class="absolute left-0 top-full z-10 mt-1 hidden rounded-md bg-white p-2 shadow-lg group-hover:block"
                    style="min-width: max-content"
                  >
                    <div class="max-h-48 overflow-y-auto">
                      <div v-for="item in segment.values.slice(1)" :key="item.id" class="mb-2 hover:bg-muted/50">
                        <router-link
                          class="flex items-center gap-x-2"
                          :to="{ name: 'AudienceDetail', params: { audienceId: item.id } }"
                        >
                          <BaseChip
                            :color="segmentTypes.find((type) => type.id === segment.type)?.color"
                            :icon="segmentTypes.find((type) => type.id === segment.type)?.icon"
                            :text="item.name"
                          />
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <template v-else>
              <p class="text-sm text-muted-foreground">-</p>
            </template>
          </div>
        </template>
      </div>

      <div v-else class="text-center">
        <BaseIcon name="HeroSquare3Stack3DIconOutline" class="mx-auto h-12 w-12 text-muted-foreground" />
        <h2 class="mt-2 text-base font-semibold leading-6 text-foreground">
          {{ t('pages.customer.customerDetailPage.blocks.list.emptyState.title') }}
        </h2>
        <span class="text-xs text-muted-foreground">
          {{ t('pages.customer.customerPage.blocks.create.sections.segmentation.caption') }}
          <router-link :to="{ name: 'AudienceList', hash: '#create' }" class="text-primary underline">
            {{ t('pages.customer.customerPage.blocks.create.sections.segmentation.captionLink') }}
          </router-link>
        </span>
      </div>
    </div>
  </div>
</template>
